/**
 * 逻辑流编辑器 - Zustand 状态管理
 */

import {create} from 'zustand';
import type {
    LogicFlowEdge,
    LogicFlowNode,
    LogicFlowNodeConfig,
    LogicFlowNodeType,
    StrategyConfig,
    StrategyVisualModelV2,
} from '../types/logicFlow';
import {createDefaultVisualModelV2, hasCycle, INDICATOR_FUNCTION_LIBRARY, isValidConnection} from '../types/logicFlow';
import {autoLayout as doAutoLayout} from '../utils/logicFlowLayout';

export type EditorViewMode = 'flow' | 'code' | 'split';

export interface ValidationResult {
    valid: boolean;
    errors: string[];
    warnings: string[];
    generatedStrategyCode?: string;
    normalizedVisualModel?: string;
    strategyClassName?: string;
}

export interface LogicFlowState {
    // 模型数据
    visualModel: StrategyVisualModelV2;
    selectedNodeId: string | null;

    // UI 状态
    viewMode: EditorViewMode;
    validationResult: ValidationResult | null;
    isSaving: boolean;
    isDirty: boolean;
    lastSavedSnapshot: string;

    // 策略元信息
    strategyId: number | null;
    sourceType: 'FOLLOW' | 'ORIGINAL';
    prompt: string;
    isActive: boolean;

    // 操作 - 模型
    loadModel: (model: StrategyVisualModelV2, strategyId?: number | null) => void;
    resetToDefault: () => void;
    updateConfig: (partial: Partial<StrategyConfig>) => void;

    // 操作 - 节点
    addNode: (type: LogicFlowNodeType) => void;
    removeNode: (id: string) => void;
    updateNodeConfig: (id: string, config: Partial<LogicFlowNodeConfig>) => void;
    updateNodeLabel: (id: string, label: string) => void;
    selectNode: (id: string | null) => void;

    // 操作 - 边
    addEdge: (edge: LogicFlowEdge) => void;
    removeEdge: (id: string) => void;

    // 操作 - 布局
    autoLayout: () => void;

    // 操作 - 校验
    setValidationResult: (result: ValidationResult | null) => void;
    markSaved: (strategyId: number, snapshot: string) => void;

    // 操作 - UI
    setViewMode: (mode: EditorViewMode) => void;
    setIsSaving: (saving: boolean) => void;
    setMetaInfo: (info: { sourceType: 'FOLLOW' | 'ORIGINAL'; prompt: string; isActive: boolean }) => void;

    // 查询
    getSelectedNode: () => LogicFlowNode | undefined;
    canAddEdge: (source: string, target: string) => boolean;
}

let nodeCounter = 0;

function nextId(prefix: string): string {
    return `${prefix}_${Date.now()}_${++nodeCounter}`;
}

function buildSnapshot(model: StrategyVisualModelV2): string {
    const nodesWithoutPosition = model.graph.nodes.map(({x, y, ...rest}) => rest);
    return JSON.stringify({
        graph: {nodes: nodesWithoutPosition, edges: model.graph.edges},
        config: model.config,
        title: model.title,
    });
}

export const useLogicFlowStore = create<LogicFlowState>((set, get) => ({
    visualModel: createDefaultVisualModelV2(),
    selectedNodeId: null,
    viewMode: 'flow',
    validationResult: null,
    isSaving: false,
    isDirty: false,
    lastSavedSnapshot: '',
    strategyId: null,
    sourceType: 'ORIGINAL',
    prompt: '',
    isActive: true,

    loadModel: (model, strategyId) => {
        const snapshot = buildSnapshot(model);
        set({
            visualModel: model,
            selectedNodeId: null,
            strategyId: strategyId ?? null,
            lastSavedSnapshot: snapshot,
            isDirty: false,
            validationResult: null,
        });
    },

    resetToDefault: () => {
        const model = createDefaultVisualModelV2();
        set({
            visualModel: model,
            selectedNodeId: null,
            strategyId: null,
            lastSavedSnapshot: '',
            isDirty: false,
            validationResult: null,
            viewMode: 'flow',
        });
    },

    updateConfig: (partial) => {
        set(state => {
            const newModel = {
                ...state.visualModel,
                config: {...state.visualModel.config, ...partial},
            };
            const snapshot = buildSnapshot(newModel);
            return {
                visualModel: newModel,
                isDirty: snapshot !== state.lastSavedSnapshot,
            };
        });
    },

    addNode: (type) => {
        set(state => {
            const id = nextId(type.toLowerCase());
            const config = createDefaultConfig(type);
            const label = getDefaultLabel(type);

            const node: LogicFlowNode = {id, type, label, config};
            const newModel = {
                ...state.visualModel,
                graph: {
                    ...state.visualModel.graph,
                    nodes: [...state.visualModel.graph.nodes, node],
                },
            };
            const snapshot = buildSnapshot(newModel);
            return {
                visualModel: newModel,
                isDirty: snapshot !== state.lastSavedSnapshot,
                selectedNodeId: id,
            };
        });
        get().autoLayout();
    },

    removeNode: (id) => {
        set(state => {
            const newNodes = state.visualModel.graph.nodes.filter(n => n.id !== id);
            const newEdges = state.visualModel.graph.edges.filter(
                e => e.source !== id && e.target !== id
            );
            const newModel = {
                ...state.visualModel,
                graph: {nodes: newNodes, edges: newEdges},
            };
            const snapshot = buildSnapshot(newModel);
            return {
                visualModel: newModel,
                isDirty: snapshot !== state.lastSavedSnapshot,
                selectedNodeId: state.selectedNodeId === id ? null : state.selectedNodeId,
            };
        });
        get().autoLayout();
    },

    updateNodeConfig: (id, config) => {
        set(state => {
            const newNodes = state.visualModel.graph.nodes.map(n => {
                if (n.id !== id) {
                    return n;
                }
                return {...n, config: {...n.config, ...config} as LogicFlowNodeConfig};
            });
            const newModel = {
                ...state.visualModel,
                graph: {...state.visualModel.graph, nodes: newNodes},
            };
            const snapshot = buildSnapshot(newModel);
            return {
                visualModel: newModel,
                isDirty: snapshot !== state.lastSavedSnapshot,
            };
        });
    },

    updateNodeLabel: (id, label) => {
        set(state => {
            const newNodes = state.visualModel.graph.nodes.map(n => {
                if (n.id !== id) {
                    return n;
                }
                return {...n, label};
            });
            const newModel = {
                ...state.visualModel,
                graph: {...state.visualModel.graph, nodes: newNodes},
            };
            const snapshot = buildSnapshot(newModel);
            return {
                visualModel: newModel,
                isDirty: snapshot !== state.lastSavedSnapshot,
            };
        });
    },

    selectNode: (id) => {
        set(state => state.selectedNodeId === id ? state : {selectedNodeId: id});
    },

    addEdge: (edge) => {
        set(state => {
            const exists = state.visualModel.graph.edges.some(
                e => e.source === edge.source && e.target === edge.target
            );
            if (exists) {
                return state;
            }

            const newEdges = [...state.visualModel.graph.edges, edge];
            const newGraph = {...state.visualModel.graph, edges: newEdges};

            if (hasCycle(newGraph)) {
                return state;
            }

            const newModel = {...state.visualModel, graph: newGraph};
            const snapshot = buildSnapshot(newModel);
            return {
                visualModel: newModel,
                isDirty: snapshot !== state.lastSavedSnapshot,
            };
        });
        get().autoLayout();
    },

    removeEdge: (id) => {
        set(state => {
            const newEdges = state.visualModel.graph.edges.filter(e => e.id !== id);
            const newModel = {
                ...state.visualModel,
                graph: {...state.visualModel.graph, edges: newEdges},
            };
            const snapshot = buildSnapshot(newModel);
            return {
                visualModel: newModel,
                isDirty: snapshot !== state.lastSavedSnapshot,
            };
        });
        get().autoLayout();
    },

    setValidationResult: (result) => {
        set({validationResult: result});
    },

    markSaved: (strategyId, snapshot) => {
        set({
            strategyId,
            lastSavedSnapshot: snapshot,
            isDirty: false,
        });
    },

    setViewMode: (mode) => {
        set({viewMode: mode});
    },

    setIsSaving: (saving) => {
        set({isSaving: saving});
    },

    setMetaInfo: (info) => {
        set({
            sourceType: info.sourceType,
            prompt: info.prompt,
            isActive: info.isActive,
        });
    },

    getSelectedNode: () => {
        const state = get();
        if (!state.selectedNodeId) {
            return undefined;
        }
        return state.visualModel.graph.nodes.find(n => n.id === state.selectedNodeId);
    },

    canAddEdge: (source, target) => {
        const state = get();
        const sourceNode = state.visualModel.graph.nodes.find(n => n.id === source);
        const targetNode = state.visualModel.graph.nodes.find(n => n.id === target);
        if (!sourceNode || !targetNode) {
            return false;
        }
        return isValidConnection(sourceNode.type, targetNode.type);
    },

    autoLayout: () => {
        const state = get();
        const {graph} = state.visualModel;
        if (0 === graph.nodes.length) {
            return;
        }
        const positions = doAutoLayout(graph);
        const newNodes = state.visualModel.graph.nodes.map(node => {
            const pos = positions.get(node.id);
            if (pos) {
                return {...node, x: pos.x, y: pos.y};
            }
            return node;
        });
        const newModel = {
            ...state.visualModel,
            graph: {...state.visualModel.graph, nodes: newNodes},
        };
        set({visualModel: newModel});
    },
}));

function createDefaultConfig(type: LogicFlowNodeType): LogicFlowNodeConfig {
    switch (type) {
        case 'INDICATOR': {
            const libEntry = INDICATOR_FUNCTION_LIBRARY.find(e => 'ta.SMA' === e.expression);
            const defaultParams = libEntry
                ? libEntry.params.map(p => ({...p}))
                : [{name: 'dataframe_column', value: 'close', type: 'column' as const}, {
                    name: 'timeperiod',
                    value: '20',
                    type: 'number' as const
                }];
            return {
                type: 'INDICATOR',
                outputType: 'column',
                indicatorName: 'indicator_1',
                function: 'ta.SMA',
                params: defaultParams,
            };
        }
        case 'RULE':
            return {
                type: 'RULE',
                logicOperator: 'AND',
                rules: [{leftOperand: "dataframe['close']", operator: '>', rightOperand: '0'}],
                action: 'enter_long',
                signalValue: '1',
            };
    }
}

function getDefaultLabel(type: LogicFlowNodeType): string {
    switch (type) {
        case 'INDICATOR':
            return '新指标';
        case 'RULE':
            return '新规则';
    }
}
